import {
  DayPicker,
  defaultDateLib,
  type DayPickerProps,
} from "react-day-picker";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { CaretLeftIcon } from "@phosphor-icons/react/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { Icon } from "../../Icons/Icon";

type CalendarVariant = "compact" | "booking";
type CalendarProps = DayPickerProps & {
  /**
   * "compact" (default) — the compact picker used inside DatePicker /
   * DateRangePicker popovers. "booking" — the large, inline, two-month
   * pickup-date calendar from the customer dashboard: 40px cells, bold
   * left-aligned captions, blue nav, and an 8px blue selected day.
   */
  variant?: CalendarVariant;
  /** Shade the Saturday/Sunday columns (the dashboard booking-calendar look). */
  shadeWeekends?: boolean;
  /**
   * Stretch the grid to fill the container width, with larger circular
   * day buttons — for a standalone calendar that owns its surface (e.g. an
   * InlineCalendar in a dialog body). Ignored by the "booking" variant.
   */
  fullWidth?: boolean;
  /**
   * Swap the "November 2025" caption for month and year dropdowns, so a date
   * years away is one click instead of dozens of arrow presses. Off by default
   * — most pickers land near today, where the plain label reads calmer. Bound
   * the years with `startMonth`/`endMonth`.
   */
  yearNavigation?: boolean;
};

function isWeekendDay(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

// One selection + "today" treatment shared by every calendar variant — the
// style from the customer dashboard pickup calendar. A day is a bold,
// brand-filled `rounded-lg` selected chip; today is a brand-subtle chip with a
// light-blue border. `!` (Tailwind important) so a day that is BOTH today and
// selected resolves to selected instead of colliding. Every variant reuses
// these, so the highlight looks identical everywhere — only cell size and
// spacing differ per variant.
//
// These target the button as a DESCENDANT (`[&_button]`), not a direct child.
// A caller can wrap the day in another element — PickupDatePicker wraps days
// that have an explanation in a Tooltip — which leaves the button a grandchild
// of the cell. With `[&>button]` every wrapped day silently lost its styling:
// "today" and the unavailable/holiday shading only rendered on days that
// happened to have no tooltip.
const SELECTED_DAY =
  "[&_button]:bg-brand! [&_button]:font-bold [&_button]:text-white! [&_button]:rounded-lg [&_button]:border-transparent! [&_button]:hover:bg-brand/90";
const TODAY_DAY =
  "[&_button]:bg-brand-subtle [&_button]:font-bold [&_button]:text-foreground [&_button]:rounded-lg [&_button]:border [&_button]:border-very-light-blue";

// The calendar uses the accent (indigo selection, subtle-accent "today",
// accent-tinted range band) and the hover/faint tones.
const compactClassNames = {
  root: "relative p-3",
  months: "flex flex-col md:flex-row gap-4",
  month: "flex flex-col gap-4",
  // Inset to the root's p-3 so the arrows align with the caption/grid edges and
  // their hover square clears the popover's rounded corners instead of poking out.
  nav: "absolute inset-x-3 top-3 flex items-center justify-between",
  button_previous:
    "size-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors",
  button_next:
    "size-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors",
  month_caption:
    "flex h-8 items-center justify-center text-sm font-semibold text-foreground",
  weekdays: "flex",
  weekday: "flex-1 text-center text-[11px] font-medium text-faint select-none",
  week: "mt-1 flex w-full",
  day: "relative size-8 p-0 text-center select-none",
  day_button:
    "size-8 rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-default",
  selected: SELECTED_DAY,
  today: TODAY_DAY,
  outside: "opacity-30",
  disabled: "opacity-30",
  range_start: `bg-brand-subtle rounded-l-lg ${SELECTED_DAY}`,
  range_end: `bg-brand-subtle rounded-r-lg ${SELECTED_DAY}`,
  range_middle:
    "bg-brand-subtle [&_button]:bg-transparent [&_button]:text-foreground [&_button]:rounded-none [&_button]:hover:bg-very-light-blue",
};

// Full-width layout: each column stretches to an equal share of the width
// and the day sits in a centered circle, so the grid fills a wide surface (a
// standalone InlineCalendar) instead of clustering at the compact 32px size.
// Same accent tokens as `compactClassNames`, only the geometry changes.
const fullWidthClassNames = {
  ...compactClassNames,
  root: "relative p-2 w-full",
  // Match the fullWidth root's p-2 (base uses p-3).
  nav: "absolute inset-x-2 top-2 flex items-center justify-between",
  months: "flex flex-col w-full",
  month: "flex flex-col gap-4 w-full",
  month_caption:
    "flex h-8 items-center justify-center text-base font-semibold text-foreground",
  weekday: "flex-1 text-center text-xs font-medium text-faint select-none",
  day: "relative flex-1 p-1 text-center select-none",
  day_button:
    "mx-auto flex size-10 items-center justify-center rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-default",
};

// The "booking" variant mirrors the customer dashboard's Cargo pickup-date
// calendar. Disabled days dim via faint *text* (not opacity) — that's the trick
// that lets a shaded weekend column stay solid behind a greyed-out weekend day.
const bookingClassNames = {
  // w-fit so the calendar hugs its two months (the nav then sits right after
  // the last month rather than floating out to a full-width edge).
  root: "relative w-fit p-2",
  months: "flex flex-col md:flex-row gap-8",
  month: "flex flex-col gap-2",
  nav: "absolute right-1 top-2 flex items-center gap-3",
  button_previous:
    "size-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors",
  button_next:
    "size-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors",
  month_caption:
    "flex h-9 items-center justify-start text-xl font-bold text-foreground",
  weekdays: "flex",
  weekday:
    "flex-1 pb-1 text-center text-sm font-normal text-muted-foreground select-none",
  week: "flex w-full",
  // The cell keeps the full 40px column width (so weekend bands stay flush),
  // but the button fill is inset to 36px so a highlighted day has a little
  // breathing room and never smacks into its neighbours.
  day: "relative flex size-10 items-center justify-center p-0 text-center select-none",
  day_button:
    "flex size-9 items-center justify-center rounded-lg text-sm font-normal text-foreground hover:bg-muted transition-colors cursor-pointer disabled:cursor-default",
  selected: SELECTED_DAY,
  today: TODAY_DAY,
  outside: "[&_button]:text-content-faint",
  disabled: "[&_button]:text-content-faint [&_button]:hover:bg-transparent",
};

// Dropdown caption, used when `yearNavigation` is on. Hubble doesn't load
// react-day-picker's stylesheet, so we style the caption ourselves: the native
// <select> is stretched invisibly over a styled label — the label is what you
// read, the select is what you click and what screen readers announce.
const dropdownClassNames = {
  dropdowns: "flex items-center gap-1",
  dropdown_root:
    "relative inline-flex items-center rounded-md px-1.5 transition-colors hover:bg-muted has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand",
  dropdown: "absolute inset-0 w-full cursor-pointer opacity-0",
  // No font styling — it inherits the caption's, so the label matches whichever
  // variant is in play.
  caption_label: "inline-flex items-center gap-0.5",
};

export function Calendar({
  variant = "compact",
  shadeWeekends = false,
  fullWidth = false,
  yearNavigation = false,
  modifiers,
  modifiersClassNames,
  components,
  ...props
}: CalendarProps) {
  const isBooking = variant === "booking";
  // Weekend shading rides on react-day-picker's modifier system; merge it under
  // any caller-supplied modifiers so callers can still add their own.
  const mergedModifiers = shadeWeekends
    ? { weekend: isWeekendDay, ...modifiers }
    : modifiers;
  const mergedModifiersClassNames = shadeWeekends
    ? { weekend: "bg-muted", ...modifiersClassNames }
    : modifiersClassNames;
  const baseClassNames = isBooking
    ? bookingClassNames
    : fullWidth
      ? fullWidthClassNames
      : compactClassNames;
  const classNames = yearNavigation
    ? { ...baseClassNames, ...dropdownClassNames }
    : baseClassNames;
  return (
    <DayPicker
      showOutsideDays={!isBooking}
      // Always draw six week rows so the grid is the same height every month.
      // Without it a 5-week month collapses the popover and the arrows jump out
      // from under the cursor. Skipped for "booking", which hides outside days —
      // there the sixth row would be blank.
      fixedWeeks={!isBooking}
      classNames={classNames}
      {...(yearNavigation
        ? {
            captionLayout: "dropdown" as const,
            formatters: {
              // Abbreviated month. Both dropdowns have to clear the nav arrows
              // in the compact grid, and "September 2025" doesn't fit.
              formatMonthDropdown: (month: Date, dateLib = defaultDateLib) =>
                dateLib.format(month, "LLL"),
            },
          }
        : {})}
      {...(mergedModifiers ? { modifiers: mergedModifiers } : {})}
      {...(mergedModifiersClassNames
        ? { modifiersClassNames: mergedModifiersClassNames }
        : {})}
      components={{
        Chevron: ({ orientation }) => {
          // "down" is the caret inside a caption dropdown, not a nav arrow.
          if (orientation === "down") {
            return (
              <Icon
                icon={CaretDownIcon}
                size="xs"
                color="text-muted-foreground"
              />
            );
          }
          const bookingProps = isBooking
            ? ({ color: "text-blue-text", weight: "bold", size: "lg" } as const)
            : {};
          return orientation === "left" ? (
            <Icon icon={CaretLeftIcon} {...bookingProps} />
          ) : (
            <Icon icon={CaretRightIcon} {...bookingProps} />
          );
        },
        // Consumer components (e.g. a tooltip-wrapped Day) merge over the
        // default Chevron.
        ...components,
      }}
      {...props}
    />
  );
}
